import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@efira/ui';

import { HOTKEYS } from './hotkeys';

/**
 * Обучающий тур: затемнение, вырез вокруг элемента шага, карточка с текстом.
 *
 * Три шага эталона (docs/reference/LIVECADE_UX.md, раздел 9) - про то, с чего
 * начинается первый эфир: выбрать игру, проверить аккаунт, заглянуть в
 * настройки эфира. Тур запускается кнопкой «Гид» в подвале и один раз сам -
 * при первом входе.
 *
 * ВЫРЕЗ СДЕЛАН ТЕНЬЮ, а не четырьмя прямоугольниками: `box-shadow` с огромным
 * радиусом затемняет всё вокруг подсвеченной рамки одним элементом, и дырка
 * не разъезжается при прокрутке.
 */
export interface TourStep {
  /** Что подсветить: CSS-селектор элемента консоли. */
  target: string;
  title: string;
  text: string;
}

export const TOUR_STEPS: TourStep[] = [
  {
    target: '[data-console-mode="catalog"]',
    title: 'Выберите игру',
    text: 'Игра запускается сразу и открывается в превью на паузе. В TikTok при этом ничего не уходит: пока вы не подключили эфир, всё видно только вам.',
  },
  {
    target: '[data-section="Подключение"]',
    title: 'Проверьте аккаунт',
    text: 'Игра пойдёт на этом TikTok-аккаунте. Если аккаунтов несколько, переключить можно здесь же — до выхода в эфир.',
  },
  {
    target: '[data-section="Настройки эфира"]',
    title: 'Настройки эфира',
    text: 'Чтец комментариев, оповещения и эффекты подарков включаются здесь. Это настройки аккаунта: они действуют во всех играх.',
  },
  {
    /*
      ГОРЯЧИЕ КЛАВИШИ - ШАГ ГИДА, а не строка в документации.
      Решение владельца 2026-09-26 (6) требует, чтобы клавиши были ВИДНЫ:
      номер на паде, подсказка на кнопке и полный список здесь. Список в
      документации на другом домене увидит тот, кто и так справился.
    */
    target: '[data-right="column"]',
    title: 'Горячие клавиши',
    text: `Пады ${HOTKEYS.pads} отправляют звук в эфир, ${HOTKEYS.panic} обрывает его, ${HOTKEYS.pause} ставит игру на паузу. Клавиши не срабатывают, пока вы печатаете в поле или пока открыто окно.`,
  },
];

const SEEN_KEY = 'efira.console.tour.seen.v1';

export function tourSeen(): boolean {
  try {
    return window.localStorage.getItem(SEEN_KEY) === '1';
  } catch {
    return true;
  }
}

export function Tour({ onFinish }: { onFinish: () => void }) {
  const [step, setStep] = useState(0);
  const [box, setBox] = useState<DOMRect | null>(null);
  const current = TOUR_STEPS[step];
  const popover = useRef<HTMLDivElement>(null);
  /** Кем гид был открыт: туда возвращается фокус при закрытии. */
  const opener = useRef<HTMLElement | null>(null);

  const finish = useCallback(() => {
    try {
      window.localStorage.setItem(SEEN_KEY, '1');
    } catch {
      // Тур, показавшийся второй раз, - не повод для ошибки.
    }
    // Фокус возвращается туда, откуда гид открыли: иначе после Escape он
    // оказывается на случайном соседе - у нас это была граница колонки.
    opener.current?.focus();
    onFinish();
  }, [onFinish]);

  /**
   * ГИД ЗАБИРАЕТ ФОКУС И ДЕРЖИТ ЕГО.
   *
   * Он объявлен `role="dialog" aria-modal="true"` и накрывает консоль
   * затемнением. Если фокус остаётся снаружи, Tab уводит под затемнение - в
   * элементы, которые не видно и по которым нельзя нажать. Обещание
   * `aria-modal` при этом остаётся невыполненным.
   */
  useEffect(() => {
    opener.current = document.activeElement as HTMLElement | null;
    const node = popover.current;
    node?.querySelector<HTMLElement>('button')?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || node === null) return;

      const focusable = [...node.querySelectorAll<HTMLElement>('button')].filter(
        (element) => !element.hasAttribute('disabled'),
      );
      const first = focusable[0];
      const last = focusable.at(-1);
      if (first === undefined || last === undefined) return;

      const active = document.activeElement;
      if (event.shiftKey && (active === first || !node.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !node.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  // Положение выреза меряется наблюдателем, а не в теле эффекта: правило
  // проекта запрещает setState в теле эффекта.
  useEffect(() => {
    const node = current === undefined ? null : document.querySelector(current.target);
    if (node === null) {
      return;
    }

    const observer = new ResizeObserver(() => {
      setBox(node.getBoundingClientRect());
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [current]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') finish();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [finish]);

  if (current === undefined) return null;

  const last = step === TOUR_STEPS.length - 1;
  const padding = 6;

  return (
    <div
      data-tour
      className="fixed inset-0 z-[90]"
      /* eslint-disable-next-line no-restricted-syntax -- тур: слой поверх
         всей консоли с ОТВЕРСТИЕМ в месте подсказки. `Dialog` пресета
         затемнил бы ровно тот элемент, на который тур показывает, и увёл
         бы в него фокус; в реестре shadcn пошаговой подсказки нет (искали
         tour, onboarding, coachmark, walkthrough). Роль и модальность
         остаются нашими. */
      role="dialog"
      aria-modal="true"
      aria-label="Гид по консоли"
    >
      <div data-tour-backdrop className="absolute inset-0 bg-black/50" />
      {box !== null && (
        <div
          data-tour-spotlight
          className="ring-primary pointer-events-none absolute rounded-lg ring-2"
          style={{
            left: box.left - padding,
            top: box.top - padding,
            width: box.width + padding * 2,
            height: box.height + padding * 2,
            /* eslint-disable-next-line no-restricted-syntax -- затемнение
               вокруг отверстия. Токена затемнения у пресета нет: его
               собственная подложка окна набрана таким же способом
               (`bg-black/65` в `ui/dialog.tsx`). Значение согласовано с
               ней, чтобы слои продукта гасили фон одинаково. */
            boxShadow: 'rgb(0 0 0 / 0.65) 0 0 0 9999px',
          }}
        />
      )}

      <Card
        ref={popover}
        data-tour-popover
        className="absolute w-80"
        style={
          box === null
            ? { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }
            : {
                left: Math.min(Math.max(12, box.left), window.innerWidth - 332),
                top: Math.min(box.bottom + 12, window.innerHeight - 220),
              }
        }
      >
        <CardHeader>
          <CardTitle asChild>
            <h2>{current.title}</h2>
          </CardTitle>
          <CardDescription>{current.text}</CardDescription>
        </CardHeader>

        <CardContent className="flex items-center gap-2">
          <span data-tour-dots className="flex gap-2" aria-hidden="true">
            {TOUR_STEPS.map((item, index) => (
              <span
                key={item.target}
                data-tour-dot={index === step ? 'current' : 'rest'}
                className={`size-1.5 rounded-full ${index === step ? 'bg-primary' : 'bg-muted-foreground/40'}`}
              />
            ))}
          </span>
          <span className="sr-only" aria-live="polite">
            Шаг {step + 1} из {TOUR_STEPS.length}
          </span>

          <Button variant="ghost" size="s" className="ms-auto" onClick={finish}>
            Пропустить
          </Button>
          {step > 0 && (
            <Button
              variant="secondary"
              size="s"
              onClick={() => {
                setStep((value) => value - 1);
              }}
            >
              Назад
            </Button>
          )}
          <Button
            variant="primary"
            size="s"
            onClick={() => {
              if (last) finish();
              else setStep((value) => value + 1);
            }}
          >
            {last ? 'Готово' : 'Далее'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
